import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { switchMap, take, catchError } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Environment } from '@env/environment';
import { AuthService } from './auth.service';

export class HttpOperationErrorResponse extends HttpErrorResponse {
	constructor(
		original: HttpErrorResponse,
		public readonly operationId: string
	) {
		super({
			error: original.error,
			headers: original.headers,
			status: original.status,
			statusText: original.statusText,
			url: original.url,
		});
	}
}

/**
 * Intercepts HttpRequests and injects Authorization request header when applicable.
 */
@Injectable({
	providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
	private readonly serviceUrls: string[] = [];
	private readonly authorizationHeaderKey = 'Authorization';

	constructor(
		private readonly authService: AuthService,
		private readonly environment: Environment
	) {
		// Save all of the service URLs in a local variable
		Object.keys(this.environment.services).forEach((key) => this.serviceUrls.push(this.environment.services[key]));
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Set operation ID for tracing where possible
		req = this.setTraceContext(req);

		// If this is a service URL, add the Authorization Header with accessToken
		if (this.isServiceUrl(req.url)) {
			return this.authService.accessToken.pipe(
				take(1),
				switchMap((accessToken) => {
					// Clone the request and add Authorization header
					const authReq = req.clone({
						setHeaders: {
							Authorization: `Bearer ${accessToken}`,
						},
					});
					return next.handle(authReq);
				}),
				catchError((error) => {
					// Handle token retrieval errors or unauthorized errors here
					// Example: Redirect to login page or logout user
					console.error('Error retrieving access token:', error);
					return throwError(() => new HttpErrorResponse(error));
				})
			);
		}

		// If not a service URL, continue with the original request
		return next.handle(req);
	}

	/**
	 * Sets an operation ID according to the W3C Proposed Trace Context Recommendation.
	 * @param request The outgoing request object.
	 * @see https://www.w3.org/TR/trace-context-1/
	 */
	private setTraceContext(request: HttpRequest<any>): HttpRequest<any> {
		const operationId = uuid().replace(/-/g, '');
		const parentSpan = uuid().replace(/-/g, '').substring(16);
		return request.clone({
			setHeaders: {
				traceparent: `00-${operationId}-${parentSpan}-01`,
			},
		});
	}

	/**
	 * Checks if the URL belongs to a service URL.
	 * @param url The URL to check.
	 * @returns True if the URL is a service URL, false otherwise.
	 */
	private isServiceUrl(url: string): boolean {
		return this.serviceUrls.some((serviceUrl) => url.startsWith(serviceUrl));
	}
}
